import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { calculateImposition } from "@/utils/imposition-calculator";
import { LayoutGrid, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onApply: (outs: number, sheetW: number, sheetL: number) => void;
}

export function ImpositionCalculatorModal({ isOpen, onClose, onApply }: Props) {
  const [sheetW, setSheetW] = useState(650);
  const [sheetL, setSheetL] = useState(860);
  const [flatW, setFlatW] = useState(210);
  const [flatL, setFlatL] = useState(297);
  const [gripper, setGripper] = useState(15);
  const [bleed, setBleed] = useState(3);

  const result = calculateImposition(sheetW, sheetL, flatW, flatL, gripper, bleed);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LayoutGrid className="w-5 h-5 text-indigo-600" />
            Công cụ Bình File Tự động
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Khổ giấy in (Width mm)</Label>
              <Input type="number" value={sheetW} onChange={e => setSheetW(Number(e.target.value))} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Khổ giấy in (Length mm)</Label>
              <Input type="number" value={sheetL} onChange={e => setSheetL(Number(e.target.value))} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Khổ trải SP (Width mm)</Label>
              <Input type="number" value={flatW} onChange={e => setFlatW(Number(e.target.value))} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Khổ trải SP (Length mm)</Label>
              <Input type="number" value={flatL} onChange={e => setFlatL(Number(e.target.value))} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Chừa nhíp (Gripper mm)</Label>
              <Input type="number" value={gripper} onChange={e => setGripper(Number(e.target.value))} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Tràn lề (Bleed mm)</Label>
              <Input type="number" value={bleed} onChange={e => setBleed(Number(e.target.value))} />
            </div>
          </div>

          <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 mt-4">
            <h4 className="text-sm font-semibold mb-2">Kết quả tối ưu</h4>
            {result.outs > 0 ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center border-b pb-1">
                  <span className="text-slate-500">Số con (Out/Up):</span>
                  <span className="font-bold text-lg text-indigo-700">{result.outs}</span>
                </div>
                <div className="flex justify-between items-center border-b pb-1">
                  <span className="text-slate-500">Kiểu xếp:</span>
                  <Badge variant="outline" className="bg-white">{result.layoutType === "VERTICAL" ? "Dọc" : "Ngang"}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Hao hụt giấy:</span>
                  <span className={result.wastePct > 30 ? "text-rose-600" : "text-emerald-600"}>
                    {result.wastePct}%
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-rose-500 text-sm">
                <AlertCircle className="w-4 h-4" /> Khổ giấy quá nhỏ!
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Hủy</Button>
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700" 
            disabled={result.outs === 0}
            onClick={() => onApply(result.outs, sheetW, sheetL)}
          >
            Áp dụng Out/Up
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
